/**
 * Structural validator for AI-compiled WEBSITE Craft.js trees.
 *
 * Unlike the email validator (which walks only `nodes[]`), website sections
 * carry deep subtrees in named `linkedNodes` slots (Hero headline/header/text/
 * buttons, Register→RegisterChild→FormInput[], Image→ImageChild …). Reachability
 * MUST follow BOTH `nodes[]` and `linkedNodes` or every linked child reads as an
 * orphan. This is the net-new piece over the email lane (migration plan §4.1).
 *
 * The page tree also contains string SENTINELS for the shared chrome slots
 * ("AppBarSection" / "FooterSection") — these are terminal leaves, not nodes.
 */

const WHITELISTED_NODES = new Set([
  // scaffold + chrome
  "PageWrapper", "AppBar", "Body", "Footer",
  // MVP sections + leaves
  "Hero", "TextSection", "TextChild", "Image", "ImageChild", "Button",
  "Register", "RegisterChild", "FormInput",
  // full vocabulary (Phase 3) — present so later emitters need no validator change
  "Login", "LoginChild", "Contact",
  "Agenda", "AgendaChild", "Speaker", "SpeakerChild",
  "Testimonials", "TestimonialsChild", "Gallery", "Numbers", "NumbersChild",
  "FaQ", "FaQChild", "Map", "Video", "Blog", "BlogChild", "Countdown",
  "DataInput", "FieldsGroup", "CompanionsGroup", "OverviewGroup", "Checkboxes",
]);

/** Terminal string sentinels for the shared appBar/footer slots. */
const STRING_SENTINELS = new Set(["AppBarSection", "FooterSection"]);

/**
 * Enum allowlists for the WS2 style props. Checked only where the prop is
 * PRESENT (absent = default = fine) and only on the node types the compiler
 * writes them to — `variant` also exists on RegisterSuccessGroup with different
 * semantics, so the check must not be global.
 */
const PROP_ENUMS: Array<{ prop: string; nodes?: Set<string>; values: Set<string> }> = [
  { prop: "reveal", values: new Set(["none", "fade", "rise"]) },
  { prop: "overlayStyle", nodes: new Set(["Hero"]), values: new Set(["scrim-bottom", "scrim-top", "scrim-left", "vignette"]) },
  { prop: "backgroundType", nodes: new Set(["Hero"]), values: new Set(["image", "video"]) },
  { prop: "variant", nodes: new Set(["Button"]), values: new Set(["solid", "pill", "ghost", "elevated"]) },
];

type Node = {
  type?: { resolvedName?: string };
  parent?: string;
  props?: Record<string, unknown>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
};

/** All child ids of a node — free children AND named linked slots. */
function childIds(node: Node): string[] {
  return [...(node.nodes ?? []), ...Object.values(node.linkedNodes ?? {})];
}

export function validateWebsiteCraftStructure(
  craftJs: string,
): { ok: true } | { ok: false; detail: string } {
  let tree: Record<string, Node | string>;
  try {
    tree = JSON.parse(craftJs) as Record<string, Node | string>;
  } catch {
    return { ok: false, detail: "unparseable JSON" };
  }

  const root = tree.ROOT;
  if (!root) return { ok: false, detail: "missing ROOT" };
  if (typeof root === "string" || root.type?.resolvedName !== "PageWrapper") {
    return { ok: false, detail: "ROOT is not a PageWrapper" };
  }

  for (const [id, entry] of Object.entries(tree)) {
    if (typeof entry === "string") {
      if (!STRING_SENTINELS.has(entry)) {
        return { ok: false, detail: `node ${id} is invalid sentinel ${entry}` };
      }
      continue;
    }
    const name = entry.type?.resolvedName;
    if (!name || !WHITELISTED_NODES.has(name)) {
      return { ok: false, detail: `node ${id} has invalid type ${name}` };
    }
    for (const { prop, nodes, values } of PROP_ENUMS) {
      if (nodes && !nodes.has(name)) continue;
      const v = entry.props?.[prop];
      if (v !== undefined && (typeof v !== "string" || !values.has(v))) {
        return { ok: false, detail: `node ${id} has invalid ${prop} ${String(v)}` };
      }
    }
    if (id !== "ROOT") {
      if (!entry.parent) return { ok: false, detail: `node ${id} has no parent` };
      if (!tree[entry.parent]) return { ok: false, detail: `node ${id} parent ${entry.parent} missing` };
    }
    for (const childId of childIds(entry)) {
      if (!tree[childId]) return { ok: false, detail: `node ${id} child ${childId} missing` };
    }
  }

  // Reachability + cycle check from ROOT following nodes[] AND linkedNodes.
  const seen = new Set<string>();
  const stack = ["ROOT"];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) return { ok: false, detail: `cycle at ${id}` };
    seen.add(id);
    const entry = tree[id];
    if (typeof entry === "string") continue; // sentinel leaf
    for (const childId of childIds(entry)) stack.push(childId);
  }
  if (seen.size !== Object.keys(tree).length) {
    return { ok: false, detail: "orphan nodes not reachable from ROOT" };
  }
  return { ok: true };
}
